import { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import useLogActivityAccess from '../hooks/useLogActivityAccess';

const NIVEL_COLOR = {
  beginner:     { bg: 'rgba(46,204,113,0.2)',  text: '#7effc0', label: 'Iniciante' },
  intermediate: { bg: 'rgba(241,196,15,0.2)',  text: '#ffe87e', label: 'Intermediário' },
  advanced:     { bg: 'rgba(231,76,60,0.2)',   text: '#ff9e91', label: 'Avançado' },
};

const S = {
  page: { minHeight: 'calc(100vh - 66px)', background: 'linear-gradient(160deg,#0a1628 0%,#1a2a4a 100%)', padding: '32px 20px', fontFamily: 'Poppins,sans-serif' },
  inner: { maxWidth: 900, margin: '0 auto' },
  title: { color: '#fff', fontSize: 'clamp(1.5rem,4vw,2.2rem)', fontWeight: 800, textAlign: 'center', marginBottom: 6 },
  subtitle: { color: 'rgba(255,255,255,0.55)', textAlign: 'center', fontSize: '0.95rem', marginBottom: 28 },
  tabs: { display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 20, flexWrap: 'wrap' },
  tab: (active) => ({ padding: '9px 26px', borderRadius: 30, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.92rem', fontFamily: 'Poppins,sans-serif', background: active ? '#1e90ff' : 'rgba(255,255,255,0.1)', color: active ? '#fff' : 'rgba(255,255,255,0.65)', transition: 'all 0.2s' }),
  search: { display: 'block', width: '100%', maxWidth: 380, margin: '0 auto 28px', padding: '11px 18px', borderRadius: 30, border: 'none', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: '0.95rem', outline: 'none', fontFamily: 'Poppins,sans-serif' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 20 },
  empty: { textAlign: 'center', color: 'rgba(255,255,255,0.4)', marginTop: 60, fontSize: '1.1rem' },
};

function playTTS(text) {
  fetch('/synt/text-to-speech', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, languageCode: 'en-US', voiceName: 'en-US-Neural2-J', pitch: 0 }),
  }).then(r => r.blob()).then(blob => new Audio(URL.createObjectURL(blob)).play()).catch(() => {});
}

function FlashCard({ item }) {
  const [flipped, setFlipped] = useState(false);
  const nivelStyle = NIVEL_COLOR[item.nivel] || NIVEL_COLOR.intermediate;

  return (
    <div
      onClick={() => setFlipped(f => !f)}
      style={{
        perspective: 1000,
        cursor: 'pointer',
        height: 240,
      }}
    >
      <div style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        transition: 'transform 0.55s',
        transformStyle: 'preserve-3d',
        transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
      }}>
        {/* FRENTE — A palavra e o falso significado */}
        <div style={{
          position: 'absolute', inset: 0,
          backfaceVisibility: 'hidden',
          background: 'rgba(255,255,255,0.06)',
          borderRadius: 18,
          border: '1px solid rgba(255,255,255,0.12)',
          padding: '24px 22px',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <span style={{ background: nivelStyle.bg, color: nivelStyle.text, borderRadius: 10, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 700 }}>
              {nivelStyle.label}
            </span>
            <button
              style={{ background: 'rgba(30,144,255,0.3)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', color: '#7ecfff', fontSize: '0.82rem' }}
              onClick={e => { e.stopPropagation(); playTTS(item.palavra_en); }}
            >
              🔊
            </button>
          </div>

          <div style={{ textAlign: 'center' }}>
            <h2 style={{ color: '#fff', fontSize: '2rem', fontWeight: 800, margin: 0 }}>{item.palavra_en}</h2>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', marginTop: 6 }}>Clique para revelar o significado</p>
          </div>

          <div style={{ background: 'rgba(231,76,60,0.15)', borderRadius: 10, padding: '10px 14px' }}>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', margin: 0, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Parece significar</p>
            <p style={{ color: '#ff9e91', fontWeight: 700, fontSize: '0.95rem', margin: 0 }}>❌ {item.parece_significar}</p>
          </div>
        </div>

        {/* VERSO — O significado real */}
        <div style={{
          position: 'absolute', inset: 0,
          backfaceVisibility: 'hidden',
          transform: 'rotateY(180deg)',
          background: 'linear-gradient(135deg,rgba(30,144,255,0.15),rgba(22,194,251,0.08))',
          borderRadius: 18,
          border: '1px solid rgba(30,144,255,0.3)',
          padding: '20px 22px',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ color: '#fff', fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>{item.palavra_en}</h2>
            <span style={{ background: nivelStyle.bg, color: nivelStyle.text, borderRadius: 10, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 700 }}>
              {nivelStyle.label}
            </span>
          </div>

          <div>
            <div style={{ background: 'rgba(46,204,113,0.15)', borderRadius: 10, padding: '10px 14px', marginBottom: 10 }}>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', margin: 0, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Significa na verdade</p>
              <p style={{ color: '#7effc0', fontWeight: 700, fontSize: '1rem', margin: 0 }}>✅ {item.traducao_pt}</p>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', margin: '4px 0 0', fontStyle: 'italic' }}>{item.real_significado_en}</p>
            </div>

            {item.exemplo_en && (
              <div>
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem', fontStyle: 'italic', margin: 0 }}>{item.exemplo_en}</p>
                {item.exemplo_pt && (
                  <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', margin: '4px 0 0' }}>{item.exemplo_pt}</p>
                )}
              </div>
            )}
          </div>

          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.72rem', textAlign: 'center', margin: 0 }}>Clique para voltar</p>
        </div>
      </div>
    </div>
  );
}

export default function FalsosCongnatos() {
  useLogActivityAccess('falsos-cognatos');
  const [items, setItems] = useState([]);
  const [nivel, setNivel] = useState('all');
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const cacheRef = useRef({});

  useEffect(() => {
    const key = nivel;
    if (cacheRef.current[key]) { setItems(cacheRef.current[key]); setLoading(false); return; }
    setLoading(true);
    const url = nivel === 'all' ? '/falsos-cognatos' : `/falsos-cognatos?nivel=${nivel}`;
    api.get(url)
      .then(r => { cacheRef.current[key] = r.data; setItems(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [nivel]);

  const filtrados = items.filter(item =>
    item.palavra_en.toLowerCase().includes(busca.toLowerCase()) ||
    item.parece_significar.toLowerCase().includes(busca.toLowerCase()) ||
    item.traducao_pt.toLowerCase().includes(busca.toLowerCase())
  );

  return (
    <div style={S.page}>
      <div style={S.inner}>
        <h1 style={S.title}>⚠️ Falsos Cognatos</h1>
        <p style={S.subtitle}>Palavras que parecem portuguesa mas significam outra coisa — clique no card para revelar!</p>

        <div style={S.tabs}>
          {[['all','Todos'],['beginner','Iniciante'],['intermediate','Intermediário'],['advanced','Avançado']].map(([k,l]) => (
            <button key={k} style={S.tab(nivel===k)} onClick={() => { setNivel(k); setBusca(''); }}>{l}</button>
          ))}
        </div>

        <input
          style={S.search}
          placeholder="🔍 Buscar palavra..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />

        {loading ? (
          <p style={S.empty}><i className="fas fa-spinner fa-spin" /> Carregando...</p>
        ) : filtrados.length === 0 ? (
          <p style={S.empty}>Nenhum falso cognato encontrado.</p>
        ) : (
          <div style={S.grid}>
            {filtrados.map(item => <FlashCard key={item.id} item={item} />)}
          </div>
        )}
      </div>
    </div>
  );
}
